import { useEffect, useState, type FormEvent } from 'react';
import { Seitenkopf } from '../components/Seitenkopf';
import { Badge } from '../components/ui/Badge';
import { Button } from '../components/ui/Button';
import { Card } from '../components/ui/Card';
import { EmptyState } from '../components/ui/EmptyState';
import { Input } from '../components/ui/Input';
import { IconBelege, IconPlus } from '../components/icons';
import { DEMO, api, ApiFehler } from '../lib/api';
import { DEMO_BELEGE, DEMO_OBJEKTE } from '../lib/demodaten';
import { KOSTENARTEN, findeKostenart } from '../lib/kostenarten';
import { formatDatum, formatEuro, parseEuroEingabe } from '../lib/format';

const feldKlasse =
  'w-full rounded-lg border border-border bg-surface px-3 py-2 text-sm text-fg outline-none focus:border-accent';
const heute = () => new Date().toISOString().slice(0, 10);

interface ObjektRef {
  id: string;
  bezeichnung: string;
}
interface BelegAnsicht {
  id: string;
  kostenartCode: string;
  lieferant: string | null;
  datum: string | null;
  betragCent: number;
  umlagefaehig: boolean;
}

interface ApiBeleg {
  id: string;
  kostenartCode: string | null;
  lieferant: string | null;
  rechnungsdatum: string | null;
  zeitraumVon: string;
  betragBruttoCent: number;
  umlagefaehig: boolean;
}

function ausApi(b: ApiBeleg): BelegAnsicht {
  return {
    id: b.id,
    kostenartCode: b.kostenartCode ?? '',
    lieferant: b.lieferant,
    datum: b.rechnungsdatum ?? b.zeitraumVon,
    betragCent: b.betragBruttoCent,
    umlagefaehig: b.umlagefaehig,
  };
}

function demoBelege(): BelegAnsicht[] {
  return DEMO_BELEGE.map((b) => ({
    id: b.id,
    kostenartCode: b.kostenartCode,
    lieferant: b.lieferant,
    datum: b.datum,
    betragCent: b.betragCent,
    umlagefaehig: b.umlagefaehig,
  }));
}

export function Belege() {
  const [objekte, setObjekte] = useState<ObjektRef[]>(
    DEMO ? DEMO_OBJEKTE.map((o) => ({ id: o.id, bezeichnung: o.bezeichnung })) : [],
  );
  const [gewaehlt, setGewaehlt] = useState<string>(DEMO ? (DEMO_OBJEKTE[0]?.id ?? '') : '');
  const [belege, setBelege] = useState<BelegAnsicht[] | null>(DEMO ? demoBelege() : null);
  const [laden, setLaden] = useState(!DEMO);
  const [fehler, setFehler] = useState<string | null>(null);
  const [formOffen, setFormOffen] = useState(false);

  useEffect(() => {
    if (DEMO) return;
    api
      .get<{ objekte: ObjektRef[] }>('/objekte')
      .then((d) => {
        setObjekte(d.objekte);
        setGewaehlt((g) => g || (d.objekte[0]?.id ?? ''));
      })
      .catch((e) => setFehler(e instanceof ApiFehler ? e.message : 'Objekte konnten nicht geladen werden.'));
  }, []);

  useEffect(() => {
    if (DEMO || !gewaehlt) return;
    let aktiv = true;
    setLaden(true);
    api
      .get<{ belege: ApiBeleg[] }>(`/belege?objektId=${gewaehlt}`)
      .then((d) => aktiv && (setBelege(d.belege.map(ausApi)), setFehler(null)))
      .catch((e) => aktiv && setFehler(e instanceof ApiFehler ? e.message : 'Belege konnten nicht geladen werden.'))
      .finally(() => aktiv && setLaden(false));
    return () => {
      aktiv = false;
    };
  }, [gewaehlt]);

  return (
    <div>
      <Seitenkopf
        titel="Belege"
        beschreibung="Eingangsrechnungen je Objekt und Zeitraum. Nicht umlagefähige Positionen (Instandhaltung, Verwaltung) werden erkannt und nicht auf die Mietparteien verteilt."
        aktion={
          <Button icon={<IconPlus groesse={18} />} onClick={() => setFormOffen((o) => !o)} disabled={!gewaehlt}>
            Beleg erfassen
          </Button>
        }
      />

      {objekte.length > 0 && (
        <div className="mb-4 max-w-xs">
          <label className="block">
            <span className="mb-1.5 block text-sm font-medium text-fg">Objekt</span>
            <select className={feldKlasse} value={gewaehlt} onChange={(e) => setGewaehlt(e.target.value)}>
              {objekte.map((o) => (
                <option key={o.id} value={o.id}>
                  {o.bezeichnung}
                </option>
              ))}
            </select>
          </label>
        </div>
      )}

      {formOffen && gewaehlt && (
        <BelegFormular
          objektId={gewaehlt}
          onAngelegt={(b) => {
            setBelege((bs) => [b, ...(bs ?? [])]);
            setFormOffen(false);
          }}
          onAbbruch={() => setFormOffen(false)}
        />
      )}

      {laden && !belege ? (
        <p className="text-sm text-fg-muted">Belege werden geladen …</p>
      ) : fehler && !belege ? (
        <Card>
          <p className="text-sm text-danger">{fehler}</p>
        </Card>
      ) : belege && belege.length === 0 ? (
        <EmptyState
          icon={<IconBelege groesse={22} />}
          titel="Noch kein Beleg erfasst"
          beschreibung="Erfassen Sie die Eingangsrechnungen dieses Objekts — sie fließen in die Betriebskostenabrechnung ein."
          aktion={<Button icon={<IconPlus groesse={18} />} onClick={() => setFormOffen(true)}>Beleg erfassen</Button>}
        />
      ) : (
        <div className="space-y-2.5">
          {(belege ?? []).map((b) => {
            const ka = findeKostenart(b.kostenartCode);
            return (
              <Card key={b.id}>
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <div className="min-w-0">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="text-sm font-semibold text-fg">
                        {ka?.bezeichnung ?? b.kostenartCode}
                      </span>
                      {b.umlagefaehig ? (
                        <Badge ton="erfolg">umlagefähig</Badge>
                      ) : (
                        <Badge ton="warnung">nicht umlagefähig</Badge>
                      )}
                    </div>
                    <p className="mt-1 truncate text-sm text-fg-muted">
                      {b.lieferant ?? 'Ohne Lieferant'}
                      {b.datum ? ` · ${formatDatum(b.datum)}` : ''}
                    </p>
                  </div>
                  <p className="shrink-0 text-base font-semibold tabellenziffern text-fg">
                    {formatEuro(b.betragCent)}
                  </p>
                </div>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}

function BelegFormular({
  objektId,
  onAngelegt,
  onAbbruch,
}: {
  objektId: string;
  onAngelegt: (b: BelegAnsicht) => void;
  onAbbruch: () => void;
}) {
  const jahr = new Date().getFullYear() - 1;
  const [kostenartCode, setKostenartCode] = useState('grundsteuer');
  const [lieferant, setLieferant] = useState('');
  const [betrag, setBetrag] = useState('');
  const [von, setVon] = useState(`${jahr}-01-01`);
  const [bis, setBis] = useState(`${jahr}-12-31`);
  const [rechnungsdatum, setRechnungsdatum] = useState(heute());
  const [sendet, setSendet] = useState(false);
  const [fehler, setFehler] = useState<string | null>(null);

  async function speichern(e: FormEvent) {
    e.preventDefault();
    setFehler(null);
    const cent = parseEuroEingabe(betrag);
    if (cent === null || cent <= 0) {
      setFehler('Bitte einen gültigen Betrag angeben (z. B. 1.234,56).');
      return;
    }
    if (bis < von) {
      setFehler('Der Belegzeitraum endet vor seinem Beginn.');
      return;
    }
    setSendet(true);
    try {
      if (!DEMO) {
        await api.post<{ beleg: ApiBeleg }>('/belege', {
          objektId,
          kostenartCode,
          lieferant: lieferant.trim() || undefined,
          betragBruttoCent: cent,
          zeitraumVon: von,
          zeitraumBis: bis,
          rechnungsdatum: rechnungsdatum || undefined,
        });
      }
      const ka = findeKostenart(kostenartCode);
      onAngelegt({
        id: `neu-${Date.now()}`,
        kostenartCode,
        lieferant: lieferant.trim() || null,
        datum: rechnungsdatum || von,
        betragCent: cent,
        // Vorschau: nicht umlagefähige Kostenarten werden auch in der Demo erkannt.
        umlagefaehig: ka ? !['instandhaltung', 'verwaltung', 'ruecklage'].includes(ka.code) : true,
      });
    } catch (e) {
      setFehler(e instanceof ApiFehler ? e.message : 'Der Beleg konnte nicht gespeichert werden.');
    } finally {
      setSendet(false);
    }
  }

  return (
    <Card titel="Neuer Beleg" className="mb-4">
      <form onSubmit={speichern} className="space-y-4">
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <label className="block">
            <span className="mb-1.5 block text-sm font-medium text-fg">Kostenart (BetrKV § 2)</span>
            <select className={feldKlasse} value={kostenartCode} onChange={(e) => setKostenartCode(e.target.value)}>
              {KOSTENARTEN.map((k) => (
                <option key={k.code} value={k.code}>
                  {k.nummer ? `${k.nummer} — ` : ''}
                  {k.bezeichnung}
                </option>
              ))}
            </select>
          </label>
          <Input label="Betrag (brutto)" inputMode="decimal" placeholder="z. B. 1.234,56" value={betrag} onChange={(e) => setBetrag(e.target.value)} />
        </div>
        <Input label="Lieferant (optional)" value={lieferant} onChange={(e) => setLieferant(e.target.value)} />
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
          <Input label="Zeitraum von" type="date" value={von} onChange={(e) => setVon(e.target.value)} />
          <Input label="Zeitraum bis" type="date" value={bis} onChange={(e) => setBis(e.target.value)} />
          <Input label="Rechnungsdatum" type="date" value={rechnungsdatum} onChange={(e) => setRechnungsdatum(e.target.value)} />
        </div>

        {fehler && <p className="text-sm font-medium text-danger">{fehler}</p>}

        <div className="flex flex-wrap gap-2">
          <Button type="submit" disabled={sendet}>
            {sendet ? 'Wird gespeichert …' : 'Beleg speichern'}
          </Button>
          <Button type="button" variante="dezent" onClick={onAbbruch}>
            Abbrechen
          </Button>
        </div>
      </form>
    </Card>
  );
}

export default Belege;
